import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HStack, Box, Text } from '@chakra-ui/react';
import InputBox from '../../components/shared/fields/InputBox';

export const extractVars = (text = '') => {
    const re = /{{\s*([a-zA-Z_][a-zA-Z0-9_]*|\d+)\s*}}/g;
    const vars = [];
    let m;
    while ((m = re.exec(text))) vars.push(m[1]);
    return vars;
};

export const validateSampleValue = (value = '') => {
    if (!value || String(value).trim() === '') return '';
    if (/^\d+$/.test(String(value).trim())) return 'Name sample cannot be just digits';
    return '';
};

export const validateVarsByType = (vars = []) => {
    if (!vars || vars.length === 0) return '';
    const bad = vars.filter(v => !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(String(v).trim()));
    if (bad.length) return `Invalid name variable(s): ${bad.join(', ')}`;
    return '';
};

export const validateVariableSpaces = (text = '') => {
    if (!text) return '';
    const varMatches = text.match(/{{\s*([^{}]+?)\s*}}/g) || [];
    const varsWithSpaces = [];
    varMatches.forEach(match => {
        const content = match.replace(/[{}]/g, '').trim();
        if (/\s/.test(content)) {
            varsWithSpaces.push(match);
        }
    });
    if (varsWithSpaces.length) return `Variable name cannot contain spaces: ${varsWithSpaces.join(', ')}`;
    return '';
};

export const validateTemplateName = (name = '') => {
    if (!name) return '';
    if (/\-/.test(String(name))) return 'Template name cannot contain hyphens (-)';
    return '';
};

export const formatPreview = (text = '', sampleValues = {}) => {
    if (!text) return '';
    return text.replace(/{{\s*([^{}]+?)\s*}}/g, (match, name) => {
        const val = sampleValues && Object.prototype.hasOwnProperty.call(sampleValues, name) ? sampleValues[name] : '';
        return val !== '' ? val : match;
    });
};

export function VariableSampleRow({ name, value, error, onChange }) {
    const [localValue, setLocalValue] = useState(value || '');
    const [localError, setLocalError] = useState(error || '');
    const debounceRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (value !== undefined && value !== localValue) setLocalValue(value || '');
        if (error !== undefined && error !== localError) setLocalError(error || '');
    }, [value, error]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const flushChange = useCallback((val) => {
        const err = validateSampleValue(val, 'name');
        setLocalError(err);
        onChange(name, val);
    }, [name, onChange]);

    const handleChange = useCallback((e) => {
        const v = e.target.value;
        setLocalValue(v);
        const err = validateSampleValue(v, 'name');
        setLocalError(err);

        try { onChange && onChange(name, v); } catch (ignore) {}

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => flushChange(v), 300);

        requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el && typeof el.focus === 'function') {
                el.focus();
                try {
                    const len = el.value ? el.value.length : 0;
                    el.setSelectionRange(len, len);
                } catch (err) {
                }
            }
        });
    }, [flushChange, name, onChange]);

    return (
        <HStack key={name} spacing={4} w="full" align="center">
            <Box display="flex" alignItems="center" justifyContent="center" px={3} py={2} borderRadius="md" border="1px solid" borderColor="gray.200" minW="140px" minH="40px">
                <Text fontSize="xs" fontWeight="semibold">{`{{${name}}}`}</Text>
            </Box>
            <Box flex={1} pt="20px">
                <InputBox
                    placeholder={`Enter content for {{${name}}}`}
                    value={localValue}
                    inputRef={inputRef}
                    invalid={!!localError}
                    errorText={localError}
                    size="md"
                    register={{ value: localValue, onChange: handleChange }}
                />
            </Box>
        </HStack>
    );
}

export default {
    extractVars,
    validateSampleValue,
    validateVarsByType,
    validateVariableSpaces,
    validateTemplateName,
    formatPreview,
    VariableSampleRow,
};